import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { ArrowLeft, CreditCard, Building2, CheckCircle } from "lucide-react";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

export default function Promotion0625() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const { toast } = useToast();

  const handleCheckout = async () => {
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          paymentMethod,
          priceId: 'mentorship_annual_promo', // Custom price ID for this promotion
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create checkout session');
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to start checkout",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        <Link href="/mentorship">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Mentorship
          </Button>
        </Link>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold">Special Mentorship Offer</CardTitle>
              <CardDescription className="text-lg mt-2">
                Join our exclusive mentorship program with special promotional pricing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pricing Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Annual Membership</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">First Year (Special Price)</span>
                    <span className="text-2xl font-bold text-blue-600">$4,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Renewal Rate (Annual)</span>
                    <span className="text-xl font-semibold">$5,000</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p>• Billed annually</p>
                  <p>• Auto-renews at standard rate</p>
                  <p>• Cancel anytime before renewal</p>
                </div>
              </div>

              {/* What's Included */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">What's Included:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Full access to all mentorship lectures and content</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Monthly live Q&A sessions with expert mentors</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Exclusive community access and networking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Downloadable resources and templates</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Priority support and consultation scheduling</span>
                  </li>
                </ul>
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-sm text-gray-500">
                  We'll use this to create your account after payment
                </p>
              </div>

              {/* Payment Method Selection */}
              <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="card">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Credit Card
                  </TabsTrigger>
                  <TabsTrigger value="ach">
                    <Building2 className="mr-2 h-4 w-4" />
                    Bank Transfer (ACH)
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="card" className="mt-4">
                  <p className="text-sm text-gray-600">
                    Pay securely with your credit or debit card. All major cards accepted.
                  </p>
                </TabsContent>
                <TabsContent value="ach" className="mt-4">
                  <p className="text-sm text-gray-600">
                    Pay directly from your bank account with lower processing fees.
                  </p>
                </TabsContent>
              </Tabs>

              {/* Checkout Button */}
              <Button 
                onClick={handleCheckout} 
                disabled={loading || !email}
                className="w-full text-lg py-6"
                size="lg"
              >
                {loading ? 'Processing...' : 'Proceed to Secure Checkout'}
              </Button>

              {/* Security Notice */}
              <div className="text-center text-sm text-gray-500">
                <p>🔒 Secure payment powered by Stripe</p>
                <p>Your payment information is encrypted and secure</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}